// L7 — Historial de capturas del Líder.
//
// El líder revisa registros de hoy y de días anteriores. La vista L2 solo
// muestra el turno de hoy; acá se ven todas las fechas con filtros de rango +
// turno. Reusa DetalleHoraDialog (drill-down) + RegistroAccionesLider, que solo
// habilita "Editar" cuando el bloque pertenece al turno activo AHORA — por lo
// que los días pasados quedan naturalmente en solo-lectura.
//
// Mobile-first, consistente con el estilo de L2 / SelectorLineaPage.

import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { HistorialRegistrosQuery, RegistroProduccionHora } from 'schemas';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { LiderLayout } from '../../components/layout/LiderLayout';
import { useAuth } from '../../lib/auth';
import {
  useCausalesParo,
  useContextoLider,
  useRegistrosHistorial,
  useSkusByLinea,
} from '../../lib/api/queries';
import { DetalleHoraDialog } from './L2CapturaPage';
import { RegistroAccionesLider } from './RegistroAccionesLider';
import { CantidadConMetaCell, CumplimientoBloqueCell } from './shared';

function fechaHoyISO(): string {
  // Fallback de UI hasta tener contexto: usa el día local. El server filtra.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function restarDias(iso: string, dias: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const base = new Date(Date.UTC(y, m - 1, d));
  base.setUTCDate(base.getUTCDate() - dias);
  return base.toISOString().slice(0, 10);
}

export function L7HistorialPage() {
  const { state, signOut } = useAuth();
  const contextoQ = useContextoLider();
  const causalesQ = useCausalesParo();

  const [desde, setDesde] = useState(() => restarDias(fechaHoyISO(), 7));
  const [hasta, setHasta] = useState(() => fechaHoyISO());
  const [turnoId, setTurnoId] = useState('');

  // Turnos disponibles para filtrar — derivados de las programaciones del
  // líder (los turnos en los que trabaja). Únicos por id.
  const turnos = useMemo(() => {
    const map = new Map<string, { id: string; numero: number }>();
    for (const p of contextoQ.data?.programaciones_hoy ?? []) {
      map.set(p.turno.id, { id: p.turno.id, numero: p.turno.numero });
    }
    return Array.from(map.values()).sort((a, b) => a.numero - b.numero);
  }, [contextoQ.data]);

  const filtros: HistorialRegistrosQuery = {
    desde,
    hasta,
    turno_id: turnoId || undefined,
    incluir_invalidados: false,
    limit: 100,
    offset: 0,
  };
  const historialQ = useRegistrosHistorial(filtros);

  const [detalle, setDetalle] = useState<RegistroProduccionHora | null>(null);
  // SKUs de la línea del registro abierto en el detalle (para mostrar nombre).
  const skusQ = useSkusByLinea(detalle?.linea_id ?? null);

  // Agrupar items por fecha (DESC).
  const grupos = useMemo(() => {
    const items = historialQ.data?.items ?? [];
    const map = new Map<string, RegistroProduccionHora[]>();
    for (const r of items) {
      const arr = map.get(r.fecha) ?? [];
      arr.push(r);
      map.set(r.fecha, arr);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([fecha, regs]) => ({
        fecha,
        registros: [...regs].sort((a, b) =>
          String(a.hora_inicio_bloque).localeCompare(String(b.hora_inicio_bloque)),
        ),
      }));
  }, [historialQ.data]);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  const currentUserId = state.claims.sub;

  return (
    <LiderLayout>
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 pb-12">
        <header className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              {contextoQ.data?.tenant.nombre ?? 'Historial'}
            </p>
            <h1 className="mt-0.5 text-xl font-medium text-fg">Historial de capturas</h1>
          </div>
          <Link
            to="/capturar"
            className="shrink-0 rounded border border-border px-2.5 py-1 text-xs font-medium text-fg-secondary transition-colors hover:bg-surface-overlay"
          >
            ← Capturar
          </Link>
        </header>

        {/* Filtros */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hist-desde">Desde</Label>
            <Input
              id="hist-desde"
              type="date"
              value={desde}
              max={hasta}
              onChange={(e) => setDesde(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hist-hasta">Hasta</Label>
            <Input
              id="hist-hasta"
              type="date"
              value={hasta}
              min={desde}
              onChange={(e) => setHasta(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hist-turno">Turno</Label>
            <Select id="hist-turno" value={turnoId} onChange={(e) => setTurnoId(e.target.value)}>
              <option value="">Todos</option>
              {turnos.map((t) => (
                <option key={t.id} value={t.id}>
                  Turno {t.numero}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {/* Estados */}
        {historialQ.isLoading ? (
          <div className="flex flex-col items-center gap-2 py-10">
            <Spinner className="size-5" />
            <p className="text-sm text-fg-tertiary">Cargando historial…</p>
          </div>
        ) : historialQ.isError ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <p className="text-sm text-crit">No pudimos cargar el historial</p>
            <p className="text-sm text-fg-secondary">
              {historialQ.error instanceof Error ? historialQ.error.message : 'Error desconocido'}
            </p>
            <Button onClick={() => historialQ.refetch()} disabled={historialQ.isFetching}>
              {historialQ.isFetching ? 'Reintentando…' : 'Reintentar'}
            </Button>
            <button onClick={() => signOut()} className="text-xs text-fg-tertiary underline">
              Cerrar sesión
            </button>
          </div>
        ) : grupos.length === 0 ? (
          <p className="py-10 text-center text-sm text-fg-tertiary">
            Sin capturas en el rango elegido.
          </p>
        ) : (
          <div className="flex flex-col gap-5">
            {grupos.map((g) => (
              <section key={g.fecha} className="flex flex-col gap-2">
                <h2 className="text-[11px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                  {formatearFechaLarga(g.fecha)} · {g.registros.length} bloque
                  {g.registros.length === 1 ? '' : 's'}
                </h2>
                <div className="overflow-hidden rounded border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-surface-overlay text-[11px] uppercase tracking-wide text-fg-tertiary">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Bloque</th>
                        <th className="px-3 py-2 text-right font-medium tabular-nums">Cant.</th>
                        <th className="px-3 py-2 text-right font-medium">Cumpl.</th>
                        <th className="px-3 py-2 text-right font-medium">Paros</th>
                        <th className="px-3 py-2 text-center font-medium">Estado</th>
                        <th className="px-3 py-2 text-right font-medium">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.registros.map((r) => (
                        <tr
                          key={r.id}
                          className="cursor-pointer border-t border-border transition-colors hover:bg-surface-overlay"
                          onClick={() => setDetalle(r)}
                        >
                          <td className="px-3 py-2 font-medium">
                            {String(r.hora_inicio_bloque).slice(0, 5)}–
                            {String(r.hora_fin_bloque).slice(0, 5)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <CantidadConMetaCell registro={r} />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <CumplimientoBloqueCell registro={r} />
                          </td>
                          <td className="px-3 py-2 text-right">
                            {(r.paros ?? []).length > 0 ? (
                              <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-warn-soft px-1.5 py-0.5 text-xs font-medium text-warn">
                                {(r.paros ?? []).length}
                              </span>
                            ) : (
                              <span className="text-xs text-fg-tertiary">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <EstadoChip registro={r} />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <RegistroAccionesLider registro={r} currentUserId={currentUserId} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        )}

        <DetalleHoraDialog
          registro={detalle}
          causales={causalesQ.data ?? []}
          skus={skusQ.data ?? []}
          onClose={() => setDetalle(null)}
        />
      </div>
    </LiderLayout>
  );
}

function EstadoChip({ registro }: { registro: RegistroProduccionHora }) {
  if (registro.validado_at) {
    return (
      <span className="rounded bg-ok-soft px-1.5 py-0.5 text-[10px] font-semibold text-ok">
        Validado
      </span>
    );
  }
  return (
    <span className="rounded bg-warn-soft px-1.5 py-0.5 text-[10px] font-semibold text-warn">
      Pendiente
    </span>
  );
}

function formatearFechaLarga(fechaIso: string): string {
  if (!fechaIso || fechaIso.length < 10) return fechaIso;
  const [y, m, d] = fechaIso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return fechaIso;
  const date = new Date(Date.UTC(y, m - 1, d));
  const fmt = new Intl.DateTimeFormat('es-CO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
  const s = fmt.format(date);
  return s.charAt(0).toUpperCase() + s.slice(1);
}
